import type { CrProvinciasTree } from '@/services/hacienda/haciendaTypes';

export type TerritorialOption = {
  value: string;
  label: string;
};

export type CrAddressParts = {
  province?: string | null;
  canton?: string | null;
  district?: string | null;
  other?: string | null;
};

const byCode = (a: TerritorialOption, b: TerritorialOption) =>
  a.value.localeCompare(b.value, undefined, { numeric: true });

export const getProvinceOptions = (
  provincias?: CrProvinciasTree | null
): TerritorialOption[] => {
  if (!provincias) return [];
  return Object.entries(provincias)
    .map(([value, province]) => ({ value, label: province.nombre }))
    .sort(byCode);
};

export const getCantonOptions = (
  provincias: CrProvinciasTree | null | undefined,
  provinceCode: string
): TerritorialOption[] => {
  const cantones = provincias?.[provinceCode]?.cantones;
  if (!cantones) return [];
  return Object.entries(cantones)
    .map(([value, canton]) => ({ value, label: canton.nombre }))
    .sort(byCode);
};

export const getDistrictOptions = (
  provincias: CrProvinciasTree | null | undefined,
  provinceCode: string,
  cantonCode: string
): TerritorialOption[] => {
  const distritos = provincias?.[provinceCode]?.cantones?.[cantonCode]?.distritos;
  if (!distritos) return [];
  return Object.entries(distritos)
    .map(([value, label]) => ({ value, label }))
    .sort(byCode);
};

const labelFor = (
  code: string,
  options: TerritorialOption[] | undefined
): string => options?.find((option) => option.value === code)?.label ?? code;

/** Human-readable CR address. Pass options from `useCrLocations` when available. */
export const formatCrAddress = (
  address?: CrAddressParts | null,
  options?: {
    provinces?: TerritorialOption[];
    cantons?: TerritorialOption[];
    districts?: TerritorialOption[];
  }
): string => {
  if (!address) return '';

  const province = address.province?.trim() || '';
  const canton = address.canton?.trim() || '';
  const district = address.district?.trim() || '';

  return [
    province ? labelFor(province, options?.provinces) : '',
    province && canton ? labelFor(canton, options?.cantons) : '',
    province && canton && district ? labelFor(district, options?.districts) : '',
    address.other?.trim() || '',
  ]
    .filter(Boolean)
    .join(', ');
};
